import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  Character,
} from "@dndModels";
import { User } from "@models";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

interface Props {
  campaign: Campaign;
  player: User;
  onUpdated: () => void; // This will trigger CampaignCharactersInput to see changes that are outside this control.
}

export default function CampaignPlayerInput({ campaign, player, onUpdated }: Props) {
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateCharacter = useCallback( async (char: Character) => {
    const res = await char_util.getCharacter({ id: char._id });
    if (res) {
      res.old_campaign_id = char.old_campaign_id;
      await api.updateCharacter(res, ["main"], true);
      const cookie_char = await char_util.getCharacterFromCookie(char._id);
      if (cookie_char !== null) {
        cookie_char.old_campaign_id = char.old_campaign_id;
        await api.updateCharacter(cookie_char, ["main"]);
      }
    }
  }, []);

  const removeCharactersForUser = useCallback( async () => {
    const user_id = player._id;
    const characters_for_user = campaign.characters.filter(o => o.owner_id === user_id);
    for (const char of characters_for_user) {
      char.old_campaign_id = "";
      debugger;
      // Remove c_char
      const c_char = char.c_char;
      if (c_char) {
        await api.deleteObject("campaign_char", c_char);
      }
      await updateCharacter(char);
    }
    setProcessing(false);
  }, [campaign, player, updateCharacter]);

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    const player_characters = campaign.characters.filter(o => o.owner_id === player._id);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          { player.use_name }&nbsp;
          { player_characters.length > 0 && `(Characters: ${ player_characters.map(o => o.name).join(", ") })` }
        </Grid>
        { player._id === campaign.owner_id ?
          <Grid size={6}>
            Owner
          </Grid>
        : api.user && campaign.owner_id === api.user._id ?
          <>
            <Grid size={3}>
              <DandDButtonBox 
                name="Promote to Owner"
                disabled={ processing || !api.online } 
                onClick={ async () => {
                  if (api.user) {
                    setProcessing(true);
                    console.error("Not Implemented");
                    // obj.player_ids = obj.player_ids.filter(o => o !== player._id);
                    // obj.player_ids.push(api.user._id);
                    // obj.owner_id = player._id;
                    // await api.updateObject("campaign", obj);
                    // setProcessing(false);
                  }
                }}
              />
            </Grid> 
            <Grid size={3}>
              <DandDButtonBox 
                name="Remove"
                disabled={ processing || !api.online } 
                onClick={ async () => {
                  const c_players = campaign.c_players;
                  if (c_players && api.user) {
                    const c_player = c_players.players.find(o => o.player_id === player._id);
                    if (c_player) {
                      c_players.players = c_players.players.filter(o => o._id !== c_player._id);
                      setProcessing(true);
                      await removeCharactersForUser();
                      await api.deleteObject("campaign_player", c_player);
                      setProcessing(true);
                      onUpdated();
                    }
                  }
                }}
              />
            </Grid> 
          </>
        : api.user && player._id === api.user._id &&
          <Grid size={6}>
            <DandDButtonBox 
              name="Leave Campaign"
              disabled={ processing || !api.online } 
              onClick={ async () => {
                const c_players = campaign.c_players;
                if (c_players && api.user) {
                  const c_player = c_players.players.find(o => o.player_id === player._id);
                  if (c_player) {
                    c_players.players = c_players.players.filter(o => o._id !== c_player._id);
                    setProcessing(true);
                    await removeCharactersForUser();
                    await api.deleteObject("campaign_player", c_player);
                    api.user.c_players = c_players.players.filter(o => o._id !== c_player._id);
                    api.user.playing_campaigns = api.user.playing_campaigns.filter(o => o._id !== c_player.campaign_id);
                    api.updateSharedSBIDs();
                    setProcessing(false);
                    setNavigateTo("/campaign");
                  }
                }
              }}
            />
          </Grid> 
        }
      </Grid>
    );
  }
}
